// Overlay runtime: aplica la config publicada sobre el deck compilado en el cliente.
// Si el fetch falla o el deck no está publicado, el deck compilado queda intacto.
//
// - resolveDeck: mapea el orden/overrides de la config sobre los SlideDef compilados.
// - buildOverrides / buildAliases: traducen la config a los inputs de la i18n.
// - fetchDeckConfig: lee el endpoint público con timeout + warm cache en sessionStorage.
//
// resolveDeck y los builders son puros (testeables sin red ni DOM).

import type {
  DeckConfig,
  PublishedPayload,
} from "@shared/deckConfig";
import type { DeckDef, SlideDef } from "@/decks/types";
import type { MessageOverrides, NamespaceAliases } from "@/i18n";

// Base del Worker propio de la app. En producción es same-origin (path relativo);
// overridable en dev si se apunta a un wrangler dev en otro puerto.
const DECK_API_BASE = (import.meta.env.VITE_DECK_API_URL ?? "").replace(/\/+$/, "");
const FETCH_TIMEOUT_MS = 2500;
const warmKey = (deckId: string) => `deck-config:${deckId}`;

/**
 * Resuelve el deck compilado (`compiled`, el component set) contra la config publicada.
 * `deckId` es el id real que se renderiza (puede diferir del component set en duplicados).
 *
 * Política de merge:
 *  - El orden lo define `config.slides`.
 *  - Ids desconocidos (slide eliminada del código) se descartan.
 *  - Slides compiladas no referenciadas (nuevas en código) se agregan al final como ocultas.
 *  - `hidden` de la config sobreescribe; si se omite, gana el default compilado.
 *  - Las notas no pasan por aquí: no viajan al visor (ver docs/vista-presentador.md).
 *  - Config sin slides (overrides vacíos) => se usa el deck compilado tal cual.
 */
export function resolveDeck(
  compiled: DeckDef,
  config: DeckConfig,
  deckId: string,
): DeckDef {
  if (config.slides.length === 0) {
    return { ...compiled, id: deckId };
  }

  const compiledById = new Map(compiled.slides.map((slide) => [slide.id, slide]));

  const ordered: SlideDef[] = [];
  const referenced = new Set<string>();
  for (const entry of config.slides) {
    const base = compiledById.get(entry.id);
    if (!base) continue; // id desconocido: descartar
    referenced.add(entry.id);
    ordered.push({
      ...base,
      hidden: entry.hidden ?? base.hidden,
    });
  }

  // Slides nuevas en código, no cubiertas por la config: al final, ocultas.
  const appended: SlideDef[] = compiled.slides
    .filter((slide) => !referenced.has(slide.id))
    .map((slide) => ({ ...slide, hidden: true }));

  return { ...compiled, id: deckId, slides: [...ordered, ...appended] };
}

/**
 * Convierte los overrides de mensajes de la config (claves relativas al namespace del deck)
 * en overrides de i18n con clave = ruta completa `deck.<deckId>.<relKey>`.
 */
export function buildOverrides(
  deckId: string,
  messages: DeckConfig["messages"],
): MessageOverrides {
  const prefix = `deck.${deckId}.`;
  const out: MessageOverrides = {};
  for (const [locale, entries] of Object.entries(messages)) {
    if (!entries) continue;
    const mapped: Record<string, string> = {};
    for (const [relKey, value] of Object.entries(entries)) {
      mapped[`${prefix}${relKey}`] = value;
    }
    out[locale as keyof MessageOverrides] = mapped;
  }
  return out;
}

/**
 * Alias de namespace para duplicados: `deck.<deckId>` -> `deck.<componentSet>`.
 * Vacío cuando el deck usa su propio component set (caso base).
 */
export function buildAliases(deckId: string, componentSet: string): NamespaceAliases {
  if (deckId === componentSet) return {};
  return { [`deck.${deckId}`]: `deck.${componentSet}` };
}

function readWarm(deckId: string): PublishedPayload | null {
  try {
    const raw = sessionStorage.getItem(warmKey(deckId));
    return raw ? (JSON.parse(raw) as PublishedPayload) : null;
  } catch {
    return null;
  }
}

function writeWarm(deckId: string, payload: PublishedPayload): void {
  try {
    sessionStorage.setItem(warmKey(deckId), JSON.stringify(payload));
  } catch {
    // sessionStorage lleno o no disponible: seguir sin warm cache.
  }
}

export { readWarm };

/**
 * Lee la config del deck. Sin `preview`, sirve la publicada y usa warm cache. Con `preview`
 * (token firmado), pide el BORRADOR y NO cachea (es efímero). Devuelve null ante cualquier
 * fallo para que el llamador caiga al deck compilado.
 */
export async function fetchDeckConfig(
  deckId: string,
  signal?: AbortSignal,
  preview?: string,
): Promise<PublishedPayload | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  // Encadena la señal externa (unmount) al abort interno.
  signal?.addEventListener("abort", () => controller.abort(), { once: true });

  const query = preview ? `?preview=${encodeURIComponent(preview)}` : "";
  try {
    const res = await fetch(
      `${DECK_API_BASE}/api/public/deck-config/${encodeURIComponent(deckId)}${query}`,
      { signal: controller.signal },
    );
    if (!res.ok) return null;
    const payload = (await res.json()) as PublishedPayload;
    if (!preview) writeWarm(deckId, payload);
    return payload;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}
